import React, { useEffect, useMemo, useState } from 'react';
import { get, ref } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { uploadDealDocument } from '../services/dealDocuments';
import { sendCmaToClient } from '../services/cmaDelivery';
import { muted, label, input } from '../pages/tools/ui';

const btnGold = { backgroundColor: '#D4AF37', color: '#1a1a1a', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' };
const btnOutline = { backgroundColor: '#ffffff', color: '#1a1a1a', border: '1px solid #D4AF37', padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' };
const btnDisabled = { opacity: 0.5, cursor: 'not-allowed' };

const MESSAGES = {
  sending_disabled: 'Sending is turned off until your text and email service is connected.',
  service_not_connected: 'Sending is not connected yet. The text and email service still needs to be turned on.',
  no_recipient: 'No phone or email is saved for this client.',
  send_failed: 'The message did not send. Try again.',
};

// Same client picker and Text / Email to Client flow as the CMA tool.
// getFile() returns the PDF as a File; buildMessage(client, channel) returns the default text.
export default function ClientSendPanel({ getFile, fileName, buildMessage }) {
  const { user, displayName } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [clientId, setClientId] = useState('');
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    get(ref(database, `users/${user.uid}/transactions`))
      .then((snap) => {
        const data = snap.val() || {};
        setTransactions(Object.keys(data).map((id) => ({ id, ...data[id] })));
      })
      .catch(() => setTransactions([]));
  }, [user]);

  const clients = useMemo(() => transactions
    .filter((t) => String(t.status || '').toLowerCase() !== 'lost')
    .map((t) => ({
      id: t.id,
      name: t.clientName || `${t.firstName || ''} ${t.lastName || ''}`.trim(),
      email: t.email || '',
      phone: t.phone || '',
      address: t.propertyAddress || t.address || '',
    }))
    .filter((c) => c.name)
    .sort((a, b) => a.name.localeCompare(b.name)), [transactions]);

  const selected = clients.find((c) => c.id === clientId) || null;
  const recipientFor = (client, channel) => (channel === 'text' ? client?.phone || '' : client?.email || '');
  const canSend = (channel) => !!selected && !!recipientFor(selected, channel);

  const open = (channel) => {
    setMsg('');
    setDialog({ channel, body: buildMessage(selected, channel) });
  };

  const confirm = async () => {
    const { channel, body } = dialog;
    setBusy(true);
    setMsg('Saving and sending...');
    try {
      const file = await getFile();
      if (!file) { setMsg('Could not create the PDF.'); return; }
      // Save a copy to this client's transaction documents first, so the link points to a stored file.
      let saved;
      try {
        saved = await uploadDealDocument(user.uid, selected.id, file, displayName || user.uid);
      } catch (err) {
        console.error('Saving the PDF to the transaction failed:', err);
        setMsg("Could not save the PDF to this client's transaction. Nothing was sent.");
        return;
      }
      const finalBody = channel === 'text' ? `${body}\n${saved.url}` : body;
      const res = await sendCmaToClient({ channel, to: recipientFor(selected, channel), body: finalBody, file, fileName });
      if (res.sent) {
        setMsg(`Sent to ${selected.name}.`);
        setDialog(null);
        return;
      }
      setMsg(MESSAGES[res.reason] || 'The message did not send.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
      <span style={label}>Send to client</span>
      <select style={input} value={clientId} onChange={(e) => { setClientId(e.target.value); setDialog(null); }}>
        <option value="">Choose a client</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.address ? ` (${c.address})` : ''}</option>)}
      </select>
      {selected && (
        <span style={muted}>{selected.phone ? `Phone: ${selected.phone}` : 'No phone saved.'} {selected.email ? `· ${selected.email}` : '· No email saved.'}</span>
      )}
      {clients.length === 0 && <span style={muted}>No clients are assigned to your account yet.</span>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <button onClick={() => open('text')} disabled={busy || !canSend('text')} style={{ ...btnGold, ...(busy || !canSend('text') ? btnDisabled : {}) }}>Text to Client</button>
        <button onClick={() => open('email')} disabled={busy || !canSend('email')} style={{ ...btnOutline, ...(busy || !canSend('email') ? btnDisabled : {}) }}>Email to Client</button>
      </div>
      {dialog && selected && (
        <div style={{ border: '1px solid #D4AF37', borderRadius: 10, padding: 16, display: 'grid', gap: 10 }}>
          <div style={{ fontWeight: 700 }}>{dialog.channel === 'text' ? 'Text' : 'Email'} to {selected.name}</div>
          <div style={muted}>To: {recipientFor(selected, dialog.channel)}</div>
          <textarea style={{ ...input, minHeight: 110, fontFamily: 'inherit' }} value={dialog.body} onChange={(e) => setDialog({ ...dialog, body: e.target.value })} />
          <div style={muted}>{dialog.channel === 'text' ? "The PDF is saved to this client's transaction documents, and the link is added to the text." : `Attached: ${fileName}. A copy is also saved to this client's transaction documents.`}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={confirm} disabled={busy} style={{ ...btnGold, ...(busy ? btnDisabled : {}) }}>Send</button>
            <button onClick={() => setDialog(null)} style={btnOutline}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <div style={muted}>{msg}</div>}
    </div>
  );
}
